<script setup lang="ts">
import { ComparisonResult } from '../lib/types';
import { formatCurrency } from '../lib/math';

defineProps<{
  result: ComparisonResult;
}>();
</script>

<template>
  <div class="card">
    <h3>Results summary</h3>
    <div class="grid">
      <div class="card" style="padding: 0.75rem">
        <h4>JDG (ryczałt 12%)</h4>
        <p class="muted">Wealth at horizon</p>
        <strong>{{ formatCurrency(result.jdg.summary.wealthEnd) }}</strong>
        <p class="muted">Consumption delivered: {{ formatCurrency(result.jdg.summary.consumptionDelivered) }}</p>
        <p class="muted">Combined utility (wealth + consumption): {{ formatCurrency(result.jdg.summary.combinedWealthAndConsumption) }}</p>
        <p class="muted">Portfolio end (net): {{ formatCurrency(result.jdg.summary.portfolioEndNet) }}</p>
        <p class="muted">Avg monthly invested: {{ formatCurrency(result.jdg.summary.monthlyInvestedAvg) }}</p>
        <p class="muted">Taxes:</p>
        <ul class="muted tight">
          <li>Income/payroll: {{ formatCurrency(result.jdg.summary.totalTaxes.income + result.jdg.summary.totalTaxes.payroll) }}</li>
          <li>Cap gains: {{ formatCurrency(result.jdg.summary.totalTaxes.capGains) }}</li>
        </ul>
      </div>
      <div class="card" style="padding: 0.75rem">
        <h4>Overengineered plan (sp. z o.o.)</h4>
        <p class="muted">Wealth at horizon</p>
        <strong>{{ formatCurrency(result.spzoo.summary.wealthEnd) }}</strong>
        <p class="muted">Consumption delivered: {{ formatCurrency(result.spzoo.summary.consumptionDelivered) }}</p>
        <p class="muted">Combined utility (wealth + consumption): {{ formatCurrency(result.spzoo.summary.combinedWealthAndConsumption) }}</p>
        <p class="muted">Portfolio end (net): {{ formatCurrency(result.spzoo.summary.portfolioEndNet) }}</p>
        <p class="muted">Avg monthly invested: {{ formatCurrency(result.spzoo.summary.monthlyInvestedAvg) }}</p>
        <p class="muted">Taxes:</p>
        <ul class="muted tight">
          <li>Payroll + PIT: {{ formatCurrency(result.spzoo.summary.totalTaxes.income + result.spzoo.summary.totalTaxes.payroll) }}</li>
          <li>CIT: {{ formatCurrency(result.spzoo.summary.totalTaxes.cit) }}</li>
          <li>Dividend/Estonian: {{ formatCurrency(result.spzoo.summary.totalTaxes.dividend + result.spzoo.summary.totalTaxes.estonianDistribution) }}</li>
          <li>Cap gains: {{ formatCurrency(result.spzoo.summary.totalTaxes.capGains) }}</li>
        </ul>
      </div>
      <div class="card" style="padding: 0.75rem">
        <h4>Comparison</h4>
        <p class="muted">Wealth delta (portfolio/cash retained)</p>
        <strong :class="result.deltaNetWorth >= 0 ? 'success' : 'warning'">
          {{ formatCurrency(result.deltaNetWorth) }}
        </strong>
        <p class="muted">
          Combined utility delta (wealth + consumption):
          {{ formatCurrency(result.deltaCombined ?? 0) }}
        </p>
        <p class="muted">Breakeven year (portfolio vs portfolio): {{ result.breakevenYear ?? 'Not reached' }}</p>
        <p class="muted">Higher value scenario (wealth): {{ result.deltaNetWorth >= 0 ? 'Sp. z o.o.' : 'JDG' }}</p>
      </div>
    </div>
    <div
      v-if="result.warnings.length"
      :class="['warning', { error: result.warnings.some(w => w.startsWith('Invariant failed')) }]"
      style="margin-top: 0.75rem"
    >
      <strong>Warnings:</strong>
      <ul>
        <li v-for="w in result.warnings" :key="w">{{ w }}</li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.tight {
  padding-left: 1rem;
  margin: 0.25rem 0;
}
</style>
